import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Input, Tooltip } from "@fluentui/react-components";
import {
    renderLabelWithInfoTooltipHeader,
    renderLabelWithInfoTooltipHeaderWithContent,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";

const EnergyAnalystsWeightedAvgSummaryGrid = ({ summaryItem = {}, setEnergyStafferTimeLogSummaryItemsState }) => {
    const [availableBankersThisWk, setAvailableBankersThisWk] = useState(
        summaryItem?.AvailableBankersThisWk ?? ""
    );

    useEffect(() => {
        setAvailableBankersThisWk(summaryItem?.AvailableBankersThisWk ?? "");
    }, [summaryItem]);

    const gridData = useMemo(() => {
        if (!summaryItem) return [];
        const bankers = parseInt(availableBankersThisWk) || 0;
        const estimatedIndex = bankers > 0
            ? Math.round(((summaryItem.CurrentWkHoursTotals ?? 0) / bankers))
            : 0;
        return [{
            ReportedActualsTotal: summaryItem.ReportedActualsTotals ?? 0,
            CurrentWkHrsTotal: summaryItem.CurrentWkHoursTotals ?? 0,
            ReportedIndex: summaryItem.ReportedIndex ? Math.round(((summaryItem.ReportedIndex))) : null,
            EstimatedIndex: estimatedIndex,
            AvailableBankersThisWk: availableBankersThisWk,  // ← add this
        }];
    }, [summaryItem, availableBankersThisWk]);

    const handleBankersChange = useCallback((e) => {
        const val = e.target.value;
        if (val === "" || (!isNaN(val) && !val.includes("."))) {
            setAvailableBankersThisWk(val);
            setEnergyStafferTimeLogSummaryItemsState(prev => ({
                ...prev,
                AvailableBankersThisWk: val
            }));
        }
    }, [setEnergyStafferTimeLogSummaryItemsState]);

    const handleBankersBlur = useCallback((e) => {
        const val = e.target.value;
        if (val === "" || (isNaN(val) || parseFloat(val) <= 0)) {
            setAvailableBankersThisWk(1);
            setEnergyStafferTimeLogSummaryItemsState(prev => ({
                ...prev,
                AvailableBankersThisWk: 1
            }));
        }
    }, [setEnergyStafferTimeLogSummaryItemsState]);

    const bankersCell = useCallback((props) => (
        <td className="summary-cell k-table-td">
            {
                summaryItem.ActiveTaskId === null
                    ?
                    <Tooltip content="To override, launch the weekly Staffing Capacity Tracker Project first.">

                        <Input
                            type="number"
                            min={1}
                            value={props.dataItem.AvailableBankersThisWk ?? ""}
                            onChange={handleBankersChange}
                            onBlur={handleBankersBlur}
                            disabled={true}
                        />
                    </Tooltip>
                    :
                    <Input
                        type="number"
                        value={props.dataItem.AvailableBankersThisWk ?? ""}
                        onKeyDown={(e) => [".", "e", "+", "-"].includes(e.key) && e.preventDefault()}
                        onChange={handleBankersChange}
                        min={1}
                        onBlur={handleBankersBlur}
                        disabled={summaryItem.ActiveTaskId === null}
                    />
            }

        </td>
    ), [handleBankersChange, handleBankersBlur, summaryItem.ActiveTaskId]);

    return (
        <div className="summary-grid-wrapper">
            <Grid
                data={gridData}
                className="energy-banker-time-log-summary-grid"
                resizable={true}
            >
                <Column
                    field="ReportedActualsTotal"
                    title={renderLabelWithInfoTooltipHeaderWithContent("Reported Actuals (Total)", <span>Sum of <b>'Actual Total Hrs (M-S)'</b>, across all Analysts and Associates on their assigned (Live/Non-Live) Deals</span>)}
                    cell={(props) => (
                        <td className="summary-cell k-table-td">
                            {props.dataItem.ReportedActualsTotal}
                        </td>
                    )}
                />
                <Column
                    field="CurrentWkHrsTotal"
                    title={renderLabelWithInfoTooltipHeaderWithContent("Current Wk Hrs (Total)", <span>Sum of <b>'Current Wk Hrs'</b>, across all Analysts and Associates on their assigned (Live/Non-Live) Deals</span>)}
                    cell={(props) => (
                        <td className="summary-cell k-table-td">
                            {props.dataItem.CurrentWkHrsTotal}
                        </td>
                    )}
                />
                <Column
                    field="ReportedIndex"
                    title={renderLabelWithInfoTooltipHeaderWithContent("Reported Index", <span><b>'Reported Actuals (Total)'</b>, divided by the value logged to the <b>'Available Bankers this Wk'</b> ({summaryItem.AvailableBankersPrevWk} Bankers) field in the previous week</span>)}
                    cell={(props) => (
                        <td className="summary-cell k-table-td">
                            {props.dataItem.ReportedIndex ?? "—"}
                        </td>
                    )}
                />
                <Column
                    field="EstimatedIndex"
                    title={renderLabelWithInfoTooltipHeaderWithContent("Estimated Index", <span><b>'Current Wk Hrs (Total)'</b>, divided by the value logged to the <b>'Available Bankers this Wk'</b> field in the current week</span>)}
                    cell={(props) => (
                        <td className="summary-cell k-table-td">
                            {props.dataItem.EstimatedIndex ?? "—"}
                        </td>
                    )}
                />
                <Column
                    field="AvailableBankersThisWk"
                    title={renderLabelWithInfoTooltipHeaderWithContent("Available Bankers this Wk", <span><p>Number of Bankers actively engaged on assigned Deals this week. Defaults to all Bankers in an Intern role at GIB/Energy.</p>
                        <p>Use the field below to override this count for vacations or PTO — this will affect the <b>‘Estimated Index’</b> for the current week and the <b>‘Reported Index’</b> for the following week.</p></span>)}
                    cell={bankersCell}
                />
            </Grid>
        </div>
    );
};

EnergyAnalystsWeightedAvgSummaryGrid.propTypes = {
    summaryItem: PropTypes.shape({
        ReportedActualsTotals: PropTypes.number,
        CurrentWkHoursTotals: PropTypes.number,
        ReportedIndex: PropTypes.number,
        EstimatedIndex: PropTypes.number,
        AvailableBankersThisWk: PropTypes.number,
    }),
};

export default EnergyAnalystsWeightedAvgSummaryGrid;